import React from "react";

import { twJoin } from "tailwind-merge";

import { Pill } from "src/base-components/Pill";
import { Option, Select } from "src/base-components/Select";
import { Tooltip } from "src/design-system/Tooltip";
import { ModelIcon } from "src/genai/ModelSelector/ModelIcon";
import { ModelOptionBadge } from "src/genai/ModelSelector/ModelOptionBadge";
import { RegionRoutingBadge } from "src/genai/ModelSelector/RegionRoutingBadge";
import { ModelOption } from "src/genai/ModelSelector/types";

export type ModelSelectorDropdownProps = {
  /** List of available models to display */
  modelOptions: ModelOption[];
  /** Currently selected model ID */
  selectedModelId: string | undefined;
  /** Callback when a model is selected */
  onSelect: (modelId: string) => void;
  /** Whether the entire selector is disabled */
  disabled?: boolean;
  /** Whether the models are still loading */
  loading?: boolean;
  /** Placeholder text when no model is selected */
  placeholder?: string;
  /** data-loc attribute for testing */
  dataLoc?: string;
  /** Whether to show provider badges on each model option */
  showProviderBadge?: boolean;
  /** Whether to show descriptions for each model option */
  showDescription?: boolean;
};

/**
 * Reusable model selector dropdown component.
 * Can be used with different model sources (aiNode, agents).
 */
export const ModelSelectorDropdown: React.FC<ModelSelectorDropdownProps> = ({
  modelOptions,
  disabled = false,
  onSelect,
  selectedModelId,
  loading = false,
  placeholder = "Select model",
  dataLoc = "model-selector",
  showProviderBadge = true,
  showDescription = true,
}) => {
  const selectedModel = modelOptions.find((m) => m.id === selectedModelId);

  const options: Option[] = modelOptions.map((modelOption) => {
    const stateBadge = showProviderBadge ? modelOption.badgeConfig : undefined;
    const regionRouted = modelOption.routingTargetRegion;

    return {
      key: modelOption.id,
      value: (
        <Tooltip
          body={modelOption.disabledReason}
          disabled={!modelOption.disabled}
          placement="bottom"
          title={modelOption.disabled ? "Model not available" : undefined}
          asChild
        >
          <div
            className="flex items-start gap-2 px-1"
            data-loc={`select-${modelOption.id}`}
          >
            <div>
              <ModelIcon
                icon={modelOption.icon}
                iconUrl={modelOption.iconUrl}
                modelName={modelOption.displayName}
                provider={modelOption.provider}
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <div className="flex items-center gap-1.5">
                <span
                  className={twJoin(
                    "min-w-0 grow truncate font-inter-normal-13px",
                    modelOption.disabled ? "text-gray-500" : "text-gray-800",
                  )}
                >
                  {modelOption.displayName}
                </span>
                {stateBadge && <ModelOptionBadge badgeConfig={stateBadge} />}
                {regionRouted && (
                  <RegionRoutingBadge targetRegion={regionRouted} />
                )}
              </div>
              {showDescription && modelOption.description && (
                <span className="whitespace-normal break-words font-inter-normal-12px text-gray-500">
                  {modelOption.description}
                </span>
              )}
            </div>
          </div>
        </Tooltip>
      ),
      disabled: modelOption.disabled,
      searchText: `${modelOption.displayName} ${modelOption.description ?? ""}`,
    };
  });

  const buttonRenderer = (option?: Option) => {
    const modelOption = option
      ? modelOptions.find((m) => m.id === option.key)
      : selectedModel;

    // Handle case where selectedModelId doesn't match any available model
    const isDeprecatedModel = !modelOption && selectedModelId;

    return (
      <div className="flex w-full items-center gap-x-2">
        {modelOption && (
          <ModelIcon
            icon={modelOption.icon}
            iconUrl={modelOption.iconUrl}
            modelName={modelOption.displayName}
            provider={modelOption.provider}
          />
        )}
        <div
          className={twJoin(
            "min-w-0 grow truncate text-left font-inter-normal-12px",
            disabled || isDeprecatedModel ? "text-gray-500" : "text-gray-800",
          )}
        >
          {modelOption?.displayName ?? selectedModelId}
        </div>
        {isDeprecatedModel && (
          <Pill size="sm" variant="gray">
            <Pill.Text>Deprecated</Pill.Text>
          </Pill>
        )}
        {showProviderBadge && modelOption?.badgeConfig && (
          <ModelOptionBadge badgeConfig={modelOption.badgeConfig} />
        )}
      </div>
    );
  };

  const handleChange = (value: string | null) => {
    if (value) {
      onSelect(value);
    }
  };

  return (
    <div className="w-full">
      <Select
        buttonRenderer={buttonRenderer}
        dataLoc={dataLoc}
        disabled={disabled}
        loading={loading}
        options={options}
        placeholder={placeholder}
        searchable={false}
        value={selectedModelId}
        fullWidth
        onChange={handleChange}
      />
    </div>
  );
};
